/*
 * � 2020 Microsoft. All rights reserved.
 */

define('SolitaireShared/CardLogic/Supply',['require',
        'lodash',
        'absolute/debug',
        'util/eventhandler',
        'BigInteger',
        'SolitaireShared/Toolbox/PseudoRandom',
        'SolitaireShared/CardLogic/CardValue',
        'SolitaireShared/CardLogic/CardSuit',
        'SolitaireShared/CardLogic/CardOp',
        'SolitaireShared/CardLogic/Card',
        'SolitaireShared/CardLogic/CardAction',
        'SolitaireShared/CardLogic/Pile',
        'absolute/eventbus',
        'SolitaireShared/CardLogic/BakedInSeeds'],
    function (require, _, Debug, EventHandler, BigInteger, PseudoRandom, CardValue, CardSuit, CardOp, Card, CardAction, Pile, EventBus, BakedInSeeds) {
        "use strict";

        var Supply = function (gameName) {
            this.initSupply(gameName);
        };

        Supply.prototype.OnFlipCard = function (e) {

        };

        Supply.prototype.OnAnimationEnd = function (sender) {

        };

        Supply.prototype.GetCardList = function () {
            return this.CardList;
        };

        Supply.prototype.ClearCardList = function () {
            return this.CardList = [];
        };

        Supply.prototype.GetPileList = function () {
            return this.PileList;
        };


        Object.defineProperty(Supply.prototype, "CurrentTime", {
            get: function () {
                return Math.floor(this.PreciseCurrentTime);
            }
        });

        Object.defineProperty(Supply.prototype, "TimePlayed", {
            get: function () {
                return Math.floor(this.PreciseTimePlayed);
            }
        });

        Object.defineProperty(Supply.prototype, "ActionList", {
            get: function () {
                return this.ActionLog;
            }
        });

        Object.defineProperty(Supply.prototype, "PileCount", {
            get: function () {
                return this.PileList.length;
            }
        });

        Supply.prototype.ResetTime = function () {
            this.PreciseCurrentTime = this.Authority.GetStartingTime();
            this.PreciseTimePlayed = 0;

            if (this.OnTimeReset) {
                this.OnTimeReset(this);
            }
        };

        /// <summary>
        /// Add the "timeDelta" to the current game clock.
        /// (unless you are counting down, and then it will subtract instead, and raise TimeRanOut if it goes to zero).
        /// </summary>
        /// <param name="timeDelta">Change in time since the last update.</param>
        Supply.prototype.AccumulateTime = function (timeDelta) {
            if (this.shouldPauseTime) return;
            if (timeDelta > 1) {
                timeDelta = 1; // sanity check on max time delta
            }

            if (this.IsTimerCountdown) {
                if (this.PreciseCurrentTime > 0) {
                    this.PreciseCurrentTime -= timeDelta;
                    if (this.PreciseCurrentTime <= 0) {
                        // time left just hit 0
                        this.PreciseCurrentTime = 0;
                        this.OnTimeRanOutEvent.handleEvent(this);
                    }
                }
            } else {
                this.PreciseCurrentTime += timeDelta;
            }

            this.OnTimeChangeEvent.handleEvent(this.PreciseCurrentTime);

            this.PreciseTimePlayed += timeDelta;

            if (this.Authority != null) {
                this.Authority.Tick(this.PreciseTimePlayed);
            }
        };

        Object.defineProperty(Supply.prototype, "Score",
            {
                get: function () {
                    return this._Score;
                },
                set: function (value) {
                    if (this.Authority != null && value > this.Authority.GetMaxScore()) {
                        this._Score = this.Authority.GetMaxScore();
                    } else if (this.Authority != null && value < this.Authority.GetMinScore()) {
                        this._Score = this.Authority.GetMinScore();
                    } else {
                        this._Score = value;
                    }

                    this.OnScoreEvent.handleEvent(this._Score);

                }
            });


        Supply.prototype.IncrementActiveAnimations = function () {
            this._activeAnimations++;
        };

        Supply.prototype.DecrementActiveAnimations = function () {
            this._activeAnimations--;
        };

        Supply.prototype.IncrementActiveBlockingAnimations = function () {
            this._activeBlockingAnimations++;
        };

        Supply.prototype.DecrementActiveBlockingAnimations = function () {
            this._activeBlockingAnimations--;
        };

        Object.defineProperty(Supply.prototype, "WaitingForBlockingAnimation",
            {
                get: function () {
                    if (this._activeBlockingAnimations > 0)
                        return true;
                    else if (this.CardAnimator != null && this.CardAnimator.WinAnimator != null && this.CardAnimator.WinAnimator.IsAnimationRunning())
                        return true;
                    else
                        return false;
                }
            });

        Object.defineProperty(Supply.prototype, "WaitingForAnimation",
            {
                get: function () {
                    if (this._activeAnimations > 0)
                        return true;
                    return this.CardAnimator != null && this.CardAnimator.WinAnimator != null && this.CardAnimator.WinAnimator.IsAnimationRunning();
                }
            });

        Object.defineProperty(Supply.prototype, "Count",
            {
                get: function () {
                    if (this.CardList != null) {
                        return this.CardList.length;
                    }
                    return 0;
                }
            });

        Supply.ValueOrder = [CardValue.Values.Ace, CardValue.Values.Two, CardValue.Values.Three, CardValue.Values.Four, CardValue.Values.Five, CardValue.Values.Six, CardValue.Values.Seven, CardValue.Values.Eight, CardValue.Values.Nine, CardValue.Values.Ten, CardValue.Values.Jack, CardValue.Values.Queen, CardValue.Values.King];
        Supply.SuitOrder = [CardSuit.Spades, CardSuit.Hearts, CardSuit.Clubs, CardSuit.Diamonds];


        Supply.prototype.GetPile = function (pileNameOrId) {
            var num = 0;
            if (typeof pileNameOrId == "string") {
                if (this.PileIndices[pileNameOrId]) {
                    num = this.PileIndices[pileNameOrId];
                } else {
                    return null;
                }
            } else {
                num = pileNameOrId;
            }

            if (num < 0) num = -num;
            if (num >= 0 && num < this.PileList.length)
                return this.PileList[num];
            else
                return null;
        };

        Supply.prototype.GetPileNum = function (name) {
            if (this.PileIndices[name]) return this.PileIndices[name];
            else return 0;
        };


        /// <summary>
        /// Set an option on the Card Supply. This is game dependent, and consists of string key-value pairs.
        /// </summary>
        /// <param name="option">The option's name (key). Note: if this starts with a +, the option is <i>temporary</i> and will be cleared out at when starting a new game.</param>
        /// <param name="value"></param>
        Supply.prototype.SetOption = function (option, value) {
            option = option.toUpperCase();
            if (this.Options[option]) {
                if (value == null) {
                    delete this.Options[option];
                } else {
                    this.Options[option] = value;
                }
            } else if (value != null) {
                this.Options[option] = value;
            }
        };

        Supply.prototype.CleanTemporaryOptions = function () {
            var KeysToRemove = [];
            for (var key in this.Options) {
                if (key[0] == "+") {
                    KeysToRemove.push(key);

                    // remove this key from the action log!
                    for (var i = 0; i < this.ActionLog.length; i++) {
                        var action = this.ActionLog[i];
                        if (action.Ops.length == 1 &&
                            action.Ops[0].Opcode == CardOp.Op.Option &&
                            action.Ops[0].GetOptionName(CardOp.eOptionParams.Option) == key) {
                            this.ActionLog.splice(i, 1);
                            break; // only need to remove 1
                        }
                    }
                }
            }

            _.forEach(KeysToRemove, function (key) {
                this.SetOption(key, null);
            }, this);
        };

        Supply.prototype.GetOption = function (option) {
            option = option.toUpperCase();
            if (this.Options[option])
                return this.Options[option].toUpperCase();
            else
                return null;
        };

        Supply.GUID_FIELD = "+GAMEID";
        Supply.prototype.GenerateGameKey = function () {
            var newGuid = "123456789"; // TODO generate Guid.NewGuid();
            var platformID = "";
            platformID = "|WEB|";
            var launchTypeKey = "+W";
            this.SetOption(Supply.GUID_FIELD, newGuid.toUpperCase() + platformID + launchTypeKey.toUpperCase());
        };

        /// <summary>
        /// Get the GameKey from the current game, or null if there is no GameKey.
        /// </summary>
        /// <returns></returns>

        /// <summary>
        /// Get the GameKey from an ActionLog in string form, or null if there is no GameKey.
        /// </summary>
        /// <param name="actionLog"></param>
        /// <returns></returns>
        Supply.prototype.GetGameKey = function (actionLog) {
            if (!actionLog) {
                return this.GetOption(Supply.GUID_FIELD);
            }

            try {
                var guidStart = actionLog.indexOf(Supply.GUID_FIELD); // TODO StringComparison.OrdinalIgnoreCase);
                if (guidStart >= 0) {
                    guidStart += Supply.GUID_FIELD.Length + 1;
                    var guidLength = actionLog.substring(guidStart).indexOf(" ");
                    return actionLog.substring(guidStart, guidLength);
                }
            } catch (e) {
                Debug.log(e);
            }
            return null;
        };


        Supply.prototype.initSupply = function (gameName) {
            this.GameName = gameName;
            this.PileIndices = {};
            this.PileList = [];

            this.PileList.push(null);
            var rootIdx = this.AddPile("Root", "Root");
            this.RootPile = this.PileList[rootIdx];

            this.DebugShowAllCards = false;

            this.Authority = null;

            this.CardsDealt = false;
            this.HasMovedCards = false;

            this.NumDecks = 0;
            this.NumJokers = 0;
            this.NumSuits = 0;

            this.CardAnimator = null;

            this.SuppressAnimations = false;
            this.LogMoves = false;
            this.EnforceGameRules = false;

            this.ActionLog = [];

            this.IsTimerCountdown = false;

            this.PreciseCurrentTime = 0;
            this.PreciseTimePlayed = 0;

            this._deckSeed = "";

            this._Score = 0;

            this._activeAnimations = 0;
            this._activeBlockingAnimations = 0;

            this.IsWaitingForAds = false;
            this.IsGamePageLoaded = false;

            this.Options = {};

            var rand = Math.round(Math.random() * PseudoRandom.UInt32.MaxValue);
            this.RandomGenerator = new PseudoRandom(rand);
            this.RandomGenerator.GetNextRandomBytes(64);
            this.RandomGenerator.GetNextRandomBytes(64);
            this.RandomGenerator.GetNextRandomBytes(64);
            this.RandomGenerator.GetNextRandomBytes(64);

            this.UseCumulativeScoring = false;
            this._cumulativeScore = -52;


            this.OnShuffleEvent = new EventHandler();
            this.OnScoreEvent = new EventHandler();
            this.OnTimeChangeEvent = new EventHandler();
            this.OnBoardCompletedEvent = new EventHandler();
            this.OnTimeRanOutEvent = new EventHandler();
            this.OnWildCardAddedEvent = new EventHandler();
            this.OnDealEvent = new EventHandler();
            this.shouldPauseTime = false;
        };


        /// <summary>
        /// Create a new supply of cards to play a game with. The total number of cards in the supply will be ((13*NumSuits) + NumJokers) * NumDecks.
        /// </summary>
        /// <param name="numDecks">Number of decks of cards to put into this supply.</param>
        /// <param name="numSuits">Number of suits to include for each deck in the supply.</param>
        /// <param name="numJokers">Number of Jokers to include for each deck in the supply.</param>
        Supply.prototype.CreateCards = function (numDecks, numSuits, numJokers) {
            //var NumCards = numDecks * (Card.NumValues * numSuits + numJokers);
            this.NumDecks = numDecks;
            this.NumJokers = numJokers;
            this.NumSuits = numSuits;

            this.CardList = [];
            _.forEach(this.PileList, function (pile) {
                if (pile != null) pile.Clear();
            });


            var seq = 0;

            for (var deck = 0; deck < this.NumDecks; deck++) {
                for (var suit = 0; suit < this.NumSuits; suit++) {
                    for (var value = 0; value < Supply.ValueOrder.length; value++) {
                        var card = new Card(this, Supply.ValueOrder[value], Supply.SuitOrder[suit], deck, seq++);
                        this.CardList.push(card);
                    }
                    for (var joker = 0; joker < this.NumJokers; joker++) {
                        var card = new Card(this, CardValue.Values.Joker, CardSuit.None, deck, seq++);
                        this.CardList.push(card);
                    }
                }
            }

            _.forEach(this.CardList, function (card) {
                    if (card.Suit != CardSuit.None && card.Value != CardValue.Values.Joker) {
                        this.RootPile.ForceAdd(card);
                    }
                },
                this);
        };

        /// <summary>
        /// Add a new empty Pile to the Supply.
        /// </summary>
        /// <param name="name">The name of the new pile. Your game uses this to look up the pile.</param>
        /// <param name="type">The type of the new pile. Your game can use this for game logic.</param>
        /// <returns>Returns false if a pile with that name already exists, true otherwise.</returns>
        Supply.prototype.AddPile = function (name, type) {
            if (this.PileIndices[name]) {
                return this.PileIndices[name];
            }
            var newPile = new Pile(this, name, type);
            var pileNum = this.PileList.length;
            this.PileList.push(newPile);
            this.PileIndices[name] = pileNum;
            newPile.PileID = pileNum;

            return pileNum;
        };

        /// <summary>
        /// Reset all the cards back to the root. You must call this function before dealing cards.
        /// </summary>
        Supply.prototype.PrepareGame = function (resetGame) {
            if (typeof resetGame == 'undefined') {
                resetGame = true;
            }

            if (resetGame)
                this.ResetGame();

            _.forEach(this.CardList, function (card) {
                    if (card.Pile != this.RootPile) {
                        if (card.Value != CardValue.Values.Joker && card.Suit != CardSuit.None) {
                            this.RootPile.ForceAdd(card); // handles removing from the previous pile
                        } else {
                            if (card.Pile != null && card.Pile.Name != "Joker") {
                                card.Pile.ForceRemove(card);
                            }
                        }
                    }
                },
                this);
        };

        /// <summary>
        /// Reset the action log, score, and time.
        /// </summary>
        Supply.prototype.ResetGame = function () {
            this.EnforceGameRules = false;
            this.LogMoves = false;
            //this.IsTimerCountdown = false;

            this._activeAnimations = 0;
            this._activeBlockingAnimations = 0;

            if (this.ActionLog != null)
                this.ActionLog = [];

            this.HasMovedCards = false;

            this.Score = this.Authority.GetStartingScore();

            this.ResetTime();
        };

        /// <summary>
        /// Tell the system that the game has begun. You must call this function after dealing out cards.
        /// </summary>
        Supply.prototype.StartGame = function () {
            this.EnforceGameRules = true;
            this.LogMoves = true;
            this.OnDealCards();
        };

        Supply.prototype.ToString = function () {
            var output = "";
            var EmptyPiles = [];
            _.forEach(PileList, function (pile) {
                if (pile != null) {
                    output += pile.ToString();
                }
            });

            return output;
        };

        Supply.prototype.LogAction = function (action) {
            if (this.OnActionRun != null) {
                this.OnActionRun(this, EventArgs.Empty);
            }
            if (action.Timestamp > 0) {
                if ((this.IsTimerCountdown && action.Timestamp < this.CurrentTime) ||
                    (!this.IsTimerCountdown && action.Timestamp > this.CurrentTime)) {
                    // counting up, replace our current time with a higher time
                    // counting down, replace our current time with a lower time
                    this.PreciseCurrentTime = action.Timestamp;
                    this.PreciseTimePlayed = this.IsTimerCountdown ? this.Authority.GetStartingTime() - action.Timestamp : action.Timestamp;
                }
            } else {
                action.Timestamp = this.CurrentTime;
                this.PreciseTimePlayed = this.IsTimerCountdown ? this.Authority.GetStartingTime() - this.CurrentTime : this.CurrentTime;
            }

            if (this.LogMoves) {
                if (!action.IsNoop()) {
                    this.ActionLog.push(action);
                    EventBus.dispatchEvent('LogActionComplete');
                }
            }
        };


        /// <summary>
        /// Returns true if one or more non-noop actions have been made to this supply.
        /// </summary>
        /// <returns></returns>
        Supply.prototype.HasPerformedAction = function () {
            if (this.ActionLog.length == 0)
                return false;

            _.forEach(this.ActionLog, function (a) {
                if (!a.IsNoop())
                    return true;
            });

            return false;
        };

        /// <summary>
        /// Gets the number of deals that have been performed so far this game, based on the action log.
        /// </summary>
        /// <returns></returns>
        Supply.prototype.NumDealsSoFar = function () {
            var dealsSoFar = 0;
            _.forEach(this.ActionLog, function (a) {
                if (a.Ops.length == 1 && a.Ops[0].Opcode == CardOp.Op.Deal)
                    dealsSoFar++;
            });
            return dealsSoFar;
        };

        Supply.prototype.NumExtraDealsSoFar = function () {
            var extraDealsSoFar = 0;
            _.forEach(this.ActionLog, function (a) {
                if (a.Ops.length == 1 && (a.Ops[0].Opcode == CardOp.Op.TotalDealsExtra))
                    extraDealsSoFar++;
            });
            return extraDealsSoFar;
        };

        Supply.prototype.GetActionLog = function () {
            if (this.GetGameKey() == null) {
                this.GenerateGameKey();
                var optionAction = new CardAction(this);
                optionAction.Parse(["OPTION", Supply.GUID_FIELD, this.GetGameKey()]);
                optionAction.Run();
                this.LogAction(optionAction);
            }

            var log = "";
            // log all Game and Option actions first.
            _.forEach(this.ActionLog, function (a) {
                // first show all Option and Game actions.
                if (a.Ops.length == 1 && a.Ops[0].Opcode == CardOp.Op.Game) {
                    log += a.ToString();
                } else if (a.Ops.length == 1 && a.Ops[0].Opcode == CardOp.Op.Option) {
                    log += a.ToString();
                } else {
                    Debug.log('did not record: ', a.ToString())
                }
            });

            _.forEach(this.ActionLog, function (a) {
                if (a.Ops.length != 1 || (a.Ops[0].Opcode != CardOp.Op.Option && a.Ops[0].Opcode != CardOp.Op.Game)) {
                    // now show all non-Option, non-Game actions.
                    if (!a.IsNoop()) {
                        log += a.ToString();
                    }
                }
            });

            // append a no-op with the current timestamp
            var n = new CardAction(this);
            n.Parse("noop");
            n.Run();
            log += n.ToString();
            return log;
        };

        /// <summary>
        /// Returns a copy of the full action log.
        /// </summary>
        Supply.prototype.GetActionList = function () {
            return this.ActionLog;
        };

        /// <summary>
        /// Returns a copy of the action log with undos removed.
        /// </summary>
        Supply.prototype.GetCollapsedActionList = function () {
            var collapsedActions = [];
            var undoesSeen = 0; // we have to skip past 1 action per undo we've already seen
            for (var i = this.ActionLog.length - 1; i >= 0; i--) {
                var action = this.ActionLog[i];
                var numUndoes = action.GetActionUndoCount();
                // if this action contains undoes, we're gonna have to skip past them
                if (numUndoes > 0) {
                    undoesSeen += numUndoes;
                } else {
                    // action doesn't contain an undo. if we're skipping past some, skip past this one.
                    if (undoesSeen > 0) {
                        undoesSeen--;
                    } else {
                        // we're not skipping past any undoes, so add this to the undo list!
                        collapsedActions.push(action);
                    }
                }
            }
            collapsedActions.reverse();
            return collapsedActions;
        };
        Supply.prototype.CanUndoActions = function () {
            var ActionsToUndo = [];

            var UndoesSeen = 0; // we have to skip past 1 action per undo we've already seen
            for (var i = this.ActionLog.length - 1; i >= 2; i--) {
                var action = this.ActionLog[i];
                if (action.Ops[action.Ops.length - 1].OpName === "DEAL") break;
                var numUndoes = action.GetActionUndoCount();
                // if this action contains undoes, we're gonna have to skip past them
                if (numUndoes > 0) {
                    UndoesSeen += numUndoes;
                } else {
                    // action doesn't contain an undo. if we're skipping past some, skip past this one.
                    if (UndoesSeen > 0) {
                        UndoesSeen--;
                    } else {
                        // we're not skipping past any undoes, so add this to the undo list!
                        ActionsToUndo.push(action);
                    }
                }
            }
            if (this.undoBoardCalled) {
                ActionsToUndo.length = 0;
                for (var i = this.ActionLog.length - 1; i >= 2; i--) {
                    if (action.Ops[action.Ops.length - 1].OpName !== "DEAL") this.ActionLog.splice(i, 1);
                }
                this.undoBoardCalled = false;
            }
            if (this.undoCalled) {
                ActionsToUndo--;
                this.ActionLog.pop();
                this.undoCalled = false;
            }
            var ret = (ActionsToUndo.length === 0);
            return ret;
        };
        /// <summary>
        /// Undo a number of actions.
        /// </summary>
        /// <param name="numActions">Number of actions to undo.</param>
        Supply.prototype.UndoActions = function (numActions) {
            var ActionsToUndo = [];

            var UndoesSeen = 0; // we have to skip past 1 action per undo we've already seen
            for (var i = this.ActionLog.length - 1; i >= 2; i--) {
                var action = this.ActionLog[i];
                if (action.Ops[action.Ops.length - 1].OpName === "DEAL") break;
                var numUndoes = action.GetActionUndoCount();
                // if this action contains undoes, we're gonna have to skip past them
                if (numUndoes > 0) {
                    UndoesSeen += numUndoes;
                } else {
                    // action doesn't contain an undo. if we're skipping past some, skip past this one.
                    if (UndoesSeen > 0) {
                        UndoesSeen--;
                    } else {
                        // we're not skipping past any undoes, so add this to the undo list!
                        if (numActions > 0) {
                            ActionsToUndo.push(action);
                            this.ActionLog.pop();
                            this.undoCalled = true;
                            numActions--;
                        } else if (numActions < 0) {
                            ActionsToUndo.push(action);
                            this.undoBoardCalled = true;
                        }
                        if (numActions === 0) break;
                    }
                }
            }

            if (numActions === -1) {
                this.ActionLog.length = this.ActionLog.length - ActionsToUndo.length;
            }

            var actionsUndone = 0;
            _.forEach(ActionsToUndo, function (action) {
                    if ((this.GameName === 'TriPeaks') && (this.GetPile('Waste').Cards.length === 1)) return;
                    if (action.Undo()) {
                        this.Score -= action.EffectiveScore;
                        this.CumulativeScore -= action.EffectiveScore;

                        this.Authority.OnRunAction(action, true, false);
                        this.Authority.OnRunSpecialAction(action.Tag, true);
                        actionsUndone++;
                    }
                },
                this);
            return actionsUndone;
        };

        Supply.prototype.GetMoveCount = function () {
            var retval = 0;
            _.forEach(this.ActionLog, function (action) {
                var undoCount = action.GetActionUndoCount();
                if (!action.IsNoop()) {
                    if (undoCount > 0) {
                        retval -= action.GetActionUndoCount();
                    } else {
                        _.forEach(action.Ops, function (op) {
                            if (op.Opcode == CardOp.Op.Move) {
                                retval++;
                                //break;
                            }
                        });
                    }
                }
            });

            return retval;
        };

        Supply.prototype.GetRandomDeckSeed = function () {
            var s = new BigInteger(this.RandomGenerator.GetNextRandomBytes(16).join(""));
            //var s = new BigInteger("1203923498234098203948092834092834098230948230984029384");
            return s.toString();
        };

        Supply.prototype.GetBakedInDeckSeed = function () {            
            var bakedInSeed = new BakedInSeeds(this.GameName, this.Options);
            var s;
            if (bakedInSeed.randomBakedSeed)
                s = new BigInteger(bakedInSeed.randomBakedSeed);
            else {
                if (this.GameName == "FreeCell") {
                    var rnd = Math.random();
                    return Math.round(rnd * 9999999); // TODO - is this correct?
                }
                this.Options.DIFFICULTY = "RANDOM";
                s = new BigInteger(this.RandomGenerator.GetNextRandomBytes(16).join(""));
            }

            return s;
        };

        Supply.prototype.GetDeckSeedStringList = function () {
            /*var actions = this.ActionList.Where((action) => action.Ops[0].Opcode == CardOp.Op.Deal).ToList(); // TODO = what does this do?
            return actions.Select((action) => action.Ops[0].GetDeck(CardOp.eDealParams.DeckSeed)).ToList();*/
            return []; // TODO fix this
        };

        Supply.prototype.OnShuffleCards = function () {
            if (this.OnShuffleEvent != null) {
                this.OnShuffleEvent.handleEvent(this);
            }
        };

        Supply.prototype.OnDealCards = function () {
            this.ActionLog = [];

            this.LogAction(CardAction.sParse(this, ["GAME", this.GameName.toUpperCase()]));
            _.forEach(this.Options, function (value, key) {
                var a = CardAction.sParse(this, ["OPTION", key, value.toString()]);
                this.LogAction(a);
            }, this);

            this.LogAction(CardAction.sParse(this, ["DEAL", this.DeckSeed]));

            if (this.OnDealEvent != null) {
                this.OnDealEvent.handleEvent(this);
            }
            this.CardsDealt = true;
        };

        Supply.prototype.WildCardAdded = function () {
            if (this.OnWildCardAddedEvent != null) {
                this.OnWildCardAddedEvent.handleEvent(this);
            }
        };

        Supply.prototype.ForceDealEvent = function () {
            if (this.OnDealEvent != null) {
                this.OnDealEvent.handleEvent(this);
            }
        };


        Object.defineProperty(Supply.prototype, "CumulativeScore",
            {
                get: function () {
                    return this._cumulativeScore;
                },
                set: function (value) {
                    if (this.UseCumulativeScoring) {
                        this._cumulativeScore = value;
                    }
                }
            });

        Object.defineProperty(Supply.prototype, "DeckSeed",
            {
                get: function () {
                    return this._deckSeed;
                },
                set: function (value) {
                    this._deckSeed = value;
                }
            });


        return Supply;
    });

